import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getImageUrl, formatPrice } from '@/components/PropertyCard';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Eye, MessageSquare, Building2, TrendingUp, Mail, Phone, Calendar, Pencil, CheckCircle, XCircle, Clock, PlusCircle, Users, Shield, Upload, User, BarChart3, FileText, ArrowRight, MapPin, Zap, Video, Sparkles, Crown, Target } from 'lucide-react';
import LeadsTab from '@/components/LeadsTab';
import VerificationStatusCard from '@/components/VerificationStatusCard';
import NextActionStrip from '@/components/NextActionStrip';
import TakeTourButton, { useAgentGuidedTour } from '@/components/AgentGuidedTour';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgencyDashboardPage() {
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactProfiles, setContactProfiles] = useState({});
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [teamForm, setTeamForm] = useState({ email: '', password: '', name: '', phone: '' });
  const [showBrandingDialog, setShowBrandingDialog] = useState(false);
  const [brandingForm, setBrandingForm] = useState({ company_name: '', logo_url: '' });
  const [txs, setTxs] = useState([]);

  // Fires the guided tour once per new agent on first visit; also exposes replay handle
  useAgentGuidedTour();

  const headers = { Authorization: `Bearer ${token}` };
  const tt = (en, es) => language === 'es' ? es : en;
  const STATUS_ES = {
    idle: 'Inactivo', requested: 'Solicitado', scheduled: 'Programado', completed: 'Completado', skipped: 'Omitido',
    profile_incomplete: 'Perfil Incompleto', profile_submitted: 'Perfil Enviado', profile_verified: 'Perfil Verificado',
    poa_missing: 'Poder Faltante', poa_uploaded: 'Poder Subido', poa_verified: 'Poder Verificado',
    pending: 'Pendiente', in_progress: 'En Progreso', failed: 'Fallido',
    not_started: 'No Iniciado', submitted: 'Enviada', accepted: 'Aceptada', rejected: 'Rechazada', counter_offer: 'Contra Oferta',
    draft_uploaded: 'Borrador Subido', under_review: 'En Revision', changes_requested: 'Cambios Solicitados', approved: 'Aprobado',
    proof_uploaded: 'Comprobante Subido', confirmed: 'Confirmado', flagged: 'Marcado',
    registered: 'Registrado', ownership_confirmed: 'Propiedad Confirmada',
    shipment_pending: 'Envio Pendiente', in_transit: 'En Transito', delivered: 'Entregado',
  };
  const ts = (status) => {
    if (!status) return '';
    if (language === 'es') return STATUS_ES[status] || status.replace(/_/g, ' ');
    return status.replace(/_/g, ' ');
  };

  const fetchData = () => {
    if (user?.role !== 'agent') { setLoading(false); return; }
    axios.get(`${API}/agent/dashboard`, { headers })
      .then(res => {
        setData(res.data);
        if (res.data.branding) setBrandingForm({ company_name: res.data.branding.company_name || '', logo_url: res.data.branding.logo_url || '' });
      })
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [user]);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/transactions/my`, { headers })
        .then(r => setTxs(r.data.transactions || []))
        .catch(() => {});
    }
  }, [token]);

  const updateStatus = async (propertyId, status) => {
    try {
      await axios.put(`${API}/properties/${propertyId}/status`, { status }, { headers });
      toast.success(`Property ${status}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const viewContactProfiles = async (propertyId) => {
    try {
      const res = await axios.get(`${API}/agent/contact-profiles/${propertyId}`, { headers });
      setContactProfiles(prev => ({ ...prev, [propertyId]: res.data.profiles }));
    } catch { toast.error('Failed to load profiles'); }
  };

  const createTeamMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/agent/team`, teamForm, { headers });
      toast.success(tt('Team member created!', 'Miembro del equipo creado!'));
      setShowTeamDialog(false);
      setTeamForm({ email: '', password: '', name: '', phone: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeTeamMember = async (memberId) => {
    try {
      await axios.delete(`${API}/agent/team/${memberId}`, { headers });
      toast.success(tt('Member removed', 'Miembro eliminado'));
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const saveBranding = async () => {
    try {
      await axios.put(`${API}/agent/branding`, brandingForm, { headers });
      toast.success(tt('Branding updated!', 'Marca actualizada!'));
      setShowBrandingDialog(false);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  if (user?.role !== 'agent') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-testid="no-agent-access">
        <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="font-heading text-xl font-semibold text-slate-700 mb-2">{tt('Agent Access Required', 'Se Requiere Acceso de Agente')}</h2>
        <p className="text-slate-500">{tt('Contact an admin to get agent access.', 'Contacta un administrador para obtener acceso de agente.')}</p>
      </div>
    );
  }

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
    </div>
  );

  const tier = data?.tier || 'free';
  const isPro = tier === 'pro';
  const isFree = tier === 'free' || tier === '';
  const pendingProps = data?.properties?.filter(p => p.status === 'pending') || [];
  const listingLimit = data?.listing_limit || {};
  const profileCompletion = data?.profile_completion || {};

  const tierInfo = {
    free: { label: tt('Free', 'Gratis'), color: 'bg-slate-100 text-slate-600', borderColor: 'border-slate-300' },
    independent: { label: tt('Independent', 'Independiente'), color: 'bg-blue-100 text-blue-700', borderColor: 'border-blue-300' },
    pro: { label: 'Pro', color: 'bg-purple-100 text-purple-700', borderColor: 'border-purple-300' },
  };
  const currentTierInfo = tierInfo[tier] || tierInfo.free;

  const statCards = [
    { label: tt('Properties', 'Propiedades'), value: data?.total_properties || 0, icon: Building2, color: 'text-primary bg-teal-50' },
    { label: tt('Approved', 'Aprobadas'), value: data?.approved || 0, icon: CheckCircle, color: 'text-green-600 bg-green-50' },
    { label: tt('Total Views', 'Vistas Totales'), value: data?.total_views || 0, icon: Eye, color: 'text-blue-600 bg-blue-50' },
    { label: tt('Contacts', 'Contactos'), value: data?.total_contacts || 0, icon: MessageSquare, color: 'text-orange-600 bg-orange-50' },
    { label: tt('Premium Inquiries', 'Consultas Premium'), value: data?.premium_inquiries_count || 0, icon: Sparkles, color: 'text-[#0B192C] bg-[#D4AF37]/15', testid: 'stat-premium-inquiries' },
  ];

  if (isPro) {
    statCards.push(
      { label: tt('Conversion', 'Conversion'), value: `${data?.conversion_rate || 0}%`, icon: TrendingUp, color: 'text-purple-600 bg-purple-50' },
      { label: tt('Team', 'Equipo'), value: data?.team?.length || 0, icon: Users, color: 'text-indigo-600 bg-indigo-50' }
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="agency-dashboard-page">
      {/* Parent Agency Branding Banner */}
      {data?.agency && (
        <div
          className="rounded-2xl mb-5 overflow-hidden border shadow-sm"
          style={{ borderColor: (data.agency.brand_color || '#0B192C') + '30' }}
          data-testid="agency-branding-banner"
        >
          <div
            className="relative px-5 py-4 flex items-center gap-4"
            style={{
              background: data.agency.cover_image
                ? `linear-gradient(rgba(11,25,44,0.65), rgba(11,25,44,0.65)), url(${data.agency.cover_image}) center/cover`
                : `linear-gradient(135deg, ${data.agency.brand_color || '#0B192C'} 0%, ${data.agency.brand_color || '#0B192C'}dd 100%)`,
            }}
          >
            {data.agency.logo ? (
              <img
                src={data.agency.logo}
                alt={data.agency.name}
                className="w-14 h-14 rounded-xl object-cover bg-white p-1 shrink-0"
                data-testid="agency-banner-logo"
              />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <Shield className="w-7 h-7 text-white" />
              </div>
            )}
            <div className="text-white min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wider text-white/70">{tt('Member of', 'Miembro de')}</p>
              <h2 className="font-heading text-lg sm:text-xl font-semibold truncate">{data.agency.name}</h2>
              {data.agency.tagline && (
                <p className="text-sm italic text-white/85 truncate">"{data.agency.tagline}"</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tier & Profile Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">{tt('Agent Dashboard', 'Panel del Agente')}</h1>
            <Badge className={`${currentTierInfo.color} text-xs`} data-testid="agent-tier-badge">
              {currentTierInfo.label}
            </Badge>
          </div>
          {isPro && data?.branding?.company_name && (
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
              <Shield className="w-3.5 h-3.5 text-primary" />{data.branding.company_name}
              {data.branding.verified && <Badge className="bg-green-100 text-green-700 text-[10px] ml-1">{tt('Verified', 'Verificado')}</Badge>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TakeTourButton />
          {!isPro && (
            <Link to="/upgrade">
              <Button variant="outline" size="sm" className="gap-1 text-xs border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/5" data-testid="upgrade-btn">
                <Zap className="w-3.5 h-3.5" />{tt('Upgrade Plan', 'Mejorar Plan')}
              </Button>
            </Link>
          )}
          {(user?.role === 'admin' || user?.role === 'superadmin' || ['independent','pro'].includes(tier)) && (
            <Link to="/agency/bulk-upload">
              <Button variant="outline" size="sm" className="gap-1.5 border-[#0B192C] text-[#0B192C] hover:bg-[#0B192C]/5" data-testid="agency-bulk-upload-btn">
                <FileText className="w-3.5 h-3.5" />{tt('Bulk Upload', 'Carga Masiva')}
              </Button>
            </Link>
          )}
          <Link to="/submit">
            <Button className="gap-1.5 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="agency-add-property">
              <PlusCircle className="w-4 h-4" />{tt('Add Property', 'Agregar Propiedad')}
            </Button>
          </Link>
        </div>
      </div>

      {/* What's next? — single most-impactful recommended action */}
      <NextActionStrip
        profileCompletion={profileCompletion}
        listingLimit={listingLimit}
        currentTier={tier}
      />

      {/* Tier Info Card + Profile Completion + Verification */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        {/* Listing Usage */}
        <Card className={`border-2 ${currentTierInfo.borderColor}`} data-testid="listing-usage-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-sm">{tt('Listing Usage', 'Uso de Listados')}</p>
              <Badge className={currentTierInfo.color + ' text-[10px]'}>{currentTierInfo.label}</Badge>
            </div>
            {listingLimit.limit != null ? (
              <>
                <div className="flex items-end gap-1 mb-2">
                  <span className="font-heading text-2xl font-bold text-slate-900">{listingLimit.active}</span>
                  <span className="text-sm text-slate-400 mb-0.5">/ {listingLimit.limit}</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${listingLimit.active >= listingLimit.limit ? 'bg-red-400' : 'bg-primary'}`}
                    style={{ width: `${Math.min(100, (listingLimit.active / listingLimit.limit) * 100)}%` }} />
                </div>
                <p className="text-xs text-slate-400 mt-1.5">
                  {listingLimit.remaining > 0
                    ? `${listingLimit.remaining} ${tt('listings remaining', 'listados disponibles')}`
                    : tt('Limit reached. Upgrade for more!', 'Limite alcanzado. Mejora para mas!')}
                </p>
              </>
            ) : (
              <p className="text-sm text-green-600 font-medium">{tt('Unlimited Listings', 'Listados Ilimitados')}</p>
            )}
          </CardContent>
        </Card>

        {/* Profile Completion */}
        <Link to="/profile" className="block group" data-testid="profile-completion-card-link">
          <Card className="transition-all hover:border-[#D4AF37] hover:shadow-md cursor-pointer h-full" data-testid="profile-completion-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-sm">{tt('Profile Completion', 'Perfil Completo')}</p>
                <span className="font-heading font-bold text-lg text-primary">{profileCompletion.percentage || 0}%</span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${profileCompletion.percentage || 0}%` }} />
              </div>
              {profileCompletion.missing?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {profileCompletion.missing.map(field => (
                    <span key={field} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 capitalize">
                      {field === 'verified' ? tt('Get Verified', 'Verificarse') : field.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
              {profileCompletion.percentage === 100 ? (
                <p className="text-xs text-green-600">{tt('Profile complete!', 'Perfil completo!')}</p>
              ) : (
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#0B192C] group-hover:text-[#D4AF37] transition">
                  {tt('Complete profile →', 'Completar perfil →')}
                </div>
              )}
            </CardContent>
          </Card>
        </Link>

        {/* Verification Status */}
        <VerificationStatusCard />
      </div>

      {/* Stats */}
      <div className={`grid grid-cols-2 ${isPro ? 'md:grid-cols-3 lg:grid-cols-7' : 'md:grid-cols-5'} gap-3 mb-6`} data-testid="agency-stats">
        {statCards.map((s, i) => (
          <Card key={i} data-testid={s.testid}>
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.color}`}><s.icon className="w-4 h-4" /></div>
              <div><div className="font-heading text-lg font-bold">{s.value}</div><div className="text-[11px] text-slate-500 leading-tight">{s.label}</div></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending Alert */}
      {pendingProps.length > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="agency-pending-alert">
          <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <p className="font-medium text-sm text-yellow-800">
            {pendingProps.length} {tt('property(ies) pending approval', 'propiedad(es) pendiente(s)')}
          </p>
        </div>
      )}

      <Tabs defaultValue="properties" className="space-y-4">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="properties" className="rounded-lg gap-1.5" data-testid="agency-tab-properties">
            <Building2 className="w-4 h-4" />{tt('Properties', 'Propiedades')}
          </TabsTrigger>
          <TabsTrigger value="contacts" className="rounded-lg gap-1.5" data-testid="agency-tab-contacts">
            <MessageSquare className="w-4 h-4" />{tt('Contacts', 'Contactos')}
            {data?.contacts?.length > 0 && <Badge className="ml-1 bg-[#D4AF37] text-white text-xs h-5 px-1.5">{data.contacts.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="leads" className="rounded-lg gap-1.5" data-testid="agency-tab-leads">
            <Target className="w-4 h-4" />{tt('Leads', 'Leads')}
          </TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-lg gap-1.5" data-testid="agency-tab-transactions">
            <FileText className="w-4 h-4" />{tt('Transactions', 'Transacciones')}
            {txs.length > 0 && <Badge className="ml-1 bg-primary text-white text-xs h-5 px-1.5">{txs.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="tours" className="rounded-lg gap-1.5" data-testid="agency-tab-tours" onClick={() => navigate('/agent/tours')}>
            <Video className="w-4 h-4" />{tt('Tour Management', 'Gestion de Tours')}
          </TabsTrigger>
          {isPro && (
            <>
              <TabsTrigger value="team" className="rounded-lg gap-1.5" data-testid="agency-tab-team">
                <Users className="w-4 h-4" />{tt('Team', 'Equipo')}
                {data?.team?.length > 0 && <Badge className="ml-1 bg-purple-500 text-white text-xs h-5 px-1.5">{data.team.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="branding" className="rounded-lg gap-1.5" data-testid="agency-tab-branding">
                <Shield className="w-4 h-4" />{tt('Branding', 'Marca')}
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* ===== PROPERTIES TAB ===== */}
        <TabsContent value="properties">
          {data?.properties?.length > 0 ? (
            <div className="bg-white rounded-xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>{tt('Property', 'Propiedad')}</TableHead>
                    <TableHead>{tt('City', 'Ciudad')}</TableHead>
                    <TableHead>{tt('Price', 'Precio')}</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>{tt('Verification', 'Verificación')}</TableHead>
                    <TableHead>{tt('Views', 'Vistas')}</TableHead>
                    <TableHead>{tt('Contacts', 'Contactos')}</TableHead>
                    <TableHead>{tt('Actions', 'Acciones')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.properties.map(prop => {
                    const pa = data.property_analytics?.[prop.id];
                    const propContacts = data.contacts?.filter(c => c.property_id === prop.id) || [];
                    const propVerified = prop.property_verified === true;
                    const propVStatus = prop.verification_status || '';
                    const verifyPill = propVerified
                      ? { cls: 'bg-emerald-100 text-emerald-700', label: tt('Verified', 'Verificado') }
                      : propVStatus === 'paid_pending_review'
                        ? { cls: 'bg-amber-100 text-amber-700', label: tt('Pending', 'Pendiente') }
                        : propVStatus === 'rejected'
                          ? { cls: 'bg-red-100 text-red-700', label: tt('Rejected', 'Rechazada') }
                          : { cls: 'bg-slate-100 text-slate-500', label: tt('Not verified', 'No verificada') };
                    return (
                      <TableRow key={prop.id} data-testid={`agency-property-${prop.id}`}>
                        <TableCell>
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100">
                            <img src={getImageUrl(prop.main_photo)} alt="" className="w-full h-full object-cover" />
                          </div>
                        </TableCell>
                        <TableCell className="font-medium max-w-[180px] truncate">{prop.title}</TableCell>
                        <TableCell className="text-sm">{prop.city}</TableCell>
                        <TableCell className="font-semibold text-sm">{formatPrice(prop.price, prop.currency, prop.operation_type)}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${prop.status === 'approved' ? 'bg-green-100 text-green-700' : prop.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                            {t(`property.${prop.status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Link to={`/property/${prop.id}/verify`} data-testid={`prop-verify-link-${prop.id}`}>
                            <Badge className={`text-[10px] gap-1 cursor-pointer hover:opacity-80 transition ${verifyPill.cls}`}>
                              <Shield className="w-2.5 h-2.5" />{verifyPill.label}
                              {!propVerified && <span className="ml-0.5">→</span>}
                            </Badge>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 text-sm text-slate-500"><Eye className="w-3.5 h-3.5" />{prop.views_count}</span>
                        </TableCell>
                        <TableCell>
                          {propContacts.length > 0 ? (
                            <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-[#D4AF37]"
                              onClick={() => viewContactProfiles(prop.id)} data-testid={`view-contacts-${prop.id}`}>
                              <User className="w-3 h-3" />{propContacts.length}
                            </Button>
                          ) : <span className="text-xs text-slate-400">0</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => navigate(`/property/${prop.id}`)}><Eye className="w-3.5 h-3.5 text-slate-500" /></Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => navigate(`/property/${prop.id}/edit`)}><Pencil className="w-3.5 h-3.5 text-slate-500" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-border">
              <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{tt('No properties yet', 'No hay propiedades aun')}</p>
              <Link to="/submit"><Button className="mt-3 gap-1.5 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]"><PlusCircle className="w-4 h-4" />{tt('Add Property', 'Agregar Propiedad')}</Button></Link>
            </div>
          )}

          {/* Contact profiles expansion */}
          {Object.entries(contactProfiles).map(([propId, profiles]) => profiles && (
            <div key={propId} className="mt-4 bg-white rounded-xl border border-border p-4" data-testid={`contact-profiles-${propId}`}>
              <h4 className="font-semibold text-sm mb-3">{tt('User Profiles Who Contacted', 'Perfiles de Usuarios que Contactaron')}</h4>
              <div className="space-y-2">
                {profiles.map((p, i) => {
                  const isPremium = p.contact.sender_buyer_tier === 'premium';
                  return (
                    <div key={i} className={`flex items-center gap-4 p-3 rounded-lg ${isPremium ? 'bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/40' : 'bg-slate-50'}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm">{p.contact.sender_name}</p>
                          {isPremium && (
                            <Badge className="bg-[#D4AF37] text-[#0B192C] hover:bg-[#C8A028] text-[10px] gap-1 h-5" data-testid={`premium-badge-profile-${p.contact.id}`}>
                              <Crown className="w-2.5 h-2.5" />{tt('Premium', 'Premium')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">{p.contact.sender_email}</p>
                      </div>
                      {p.user_profile ? (
                        <div className="flex gap-3 text-xs text-slate-600">
                          {p.user_profile.intent && <Badge variant="outline" className="capitalize text-xs">{p.user_profile.intent}</Badge>}
                          {p.user_profile.budget_max && <span>${Number(p.user_profile.budget_max).toLocaleString()}</span>}
                          {p.user_profile.timeline && <span className="capitalize">{p.user_profile.timeline}</span>}
                          {p.user_profile.city_suburb && <span>{p.user_profile.city_suburb}</span>}
                        </div>
                      ) : <span className="text-xs text-slate-400">{tt('Unregistered user', 'Usuario no registrado')}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </TabsContent>

        {/* ===== CONTACTS TAB ===== */}
        <TabsContent value="contacts">
          {data?.contacts?.length > 0 ? (
            <div className="space-y-3">
              {data.contacts.map(contact => {
                const isPremium = contact.sender_buyer_tier === 'premium';
                return (
                  <Card key={contact.id} data-testid={`contact-${contact.id}`} className={isPremium ? 'border-[#D4AF37]/60 bg-gradient-to-r from-[#D4AF37]/5 to-transparent shadow-sm' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-sm">{contact.sender_name}</h4>
                            {isPremium && (
                              <Badge className="bg-[#D4AF37] text-[#0B192C] hover:bg-[#C8A028] text-[10px] gap-1 h-5" data-testid={`premium-badge-${contact.id}`}>
                                <Crown className="w-2.5 h-2.5" />{tt('Premium', 'Premium')}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{contact.sender_email}</span>
                            {contact.sender_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{contact.sender_phone}</span>}
                          </div>
                          <p className="text-sm text-slate-600 mt-2">{contact.message}</p>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-400 flex-shrink-0">
                          <Calendar className="w-3 h-3" />
                          {new Date(contact.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-border">
              <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{tt('No contact messages yet', 'No hay mensajes de contacto')}</p>
            </div>
          )}
        </TabsContent>

        {/* ===== LEADS TAB ===== */}
        <TabsContent value="leads">
          <LeadsTab />
        </TabsContent>

        {/* ===== TRANSACTIONS TAB ===== */}
        <TabsContent value="transactions">
          {txs.length > 0 ? (
            <div className="space-y-3">
              {txs.map(tx => {
                const prop = tx.property || {};
                const stage = tx.current_stage;
                const stageStatus = tx.stages?.[stage]?.status || '';
                const needsAction = (
                  (stage === 'tour' && stageStatus === 'requested') ||
                  (stage === 'onboarding' && stageStatus === 'profile_submitted') ||
                  (stage === 'poa' && stageStatus === 'poa_uploaded') ||
                  (stage === 'offer' && stageStatus === 'submitted') ||
                  (stage === 'payment' && stageStatus === 'proof_uploaded')
                );
                const stageLabels = {
                  tour: tt('Virtual Tour', 'Tour Virtual'), onboarding: tt('Documents', 'Documentos'),
                  poa: tt('POA', 'Poder'), due_diligence: tt('Due Diligence', 'Debida Diligencia'),
                  offer: tt('Offer', 'Oferta'), contract: tt('Contract', 'Contrato'),
                  payment: tt('Payment', 'Pago'), signing: tt('Signing', 'Firma'),
                  registry: tt('Registry', 'Registro'), complete: tt('Complete', 'Completo'),
                  delivery: tt('Delivery', 'Entrega'),
                };
                return (
                  <Link key={tx.id} to={`/transaction/${tx.id}`} data-testid={`agency-tx-${tx.id}`}>
                    <Card className={`hover:shadow-md transition-shadow cursor-pointer mb-2 ${needsAction ? 'border-amber-300 bg-amber-50/30' : ''}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-14 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                          {prop.main_photo ? (
                            <img src={typeof prop.main_photo === 'string' && prop.main_photo.startsWith('http') ? prop.main_photo : `${process.env.REACT_APP_BACKEND_URL}/api/storage/${prop.main_photo}`} alt="" className="w-full h-full object-cover" />
                          ) : <div className="w-full h-full bg-slate-200" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate">{prop.title || 'Property'}</h4>
                          <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{prop.city}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{tt('Buyer', 'Comprador')}: {tx.buyer?.name || tx.buyer?.email || '-'}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <Badge variant="outline" className="text-[10px] mb-1">{stageLabels[stage] || stage}</Badge>
                          {needsAction ? (
                            <Badge className="block text-[10px] bg-amber-500 text-white">{tt('Action Needed', 'Accion Requerida')}</Badge>
                          ) : (
                            <Badge className="block text-[10px] bg-slate-100 text-slate-500 capitalize">{ts(stageStatus)}</Badge>
                          )}
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl border border-border">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{tt('No active transactions', 'No hay transacciones activas')}</p>
            </div>
          )}
        </TabsContent>

        {/* ===== TEAM TAB (Pro Only) ===== */}
        {isPro && (
          <TabsContent value="team">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-500">{tt('Manage your team sub-accounts. Each member gets unlimited listings.', 'Gestiona las subcuentas de tu equipo. Cada miembro tiene publicaciones ilimitadas.')}</p>
              <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
                <DialogTrigger asChild>
                  <Button className="gap-1.5 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="add-team-member-btn">
                    <PlusCircle className="w-4 h-4" />{tt('Add Member', 'Agregar Miembro')}
                  </Button>
                </DialogTrigger>
                <DialogContent aria-describedby="team-dialog-desc">
                  <DialogHeader><DialogTitle>{tt('Create Team Member', 'Crear Miembro del Equipo')}</DialogTitle></DialogHeader>
                  <p id="team-dialog-desc" className="text-sm text-slate-500 mb-2">{tt('Add a new agent to your team.', 'Agrega un nuevo agente a tu equipo.')}</p>
                  <form onSubmit={createTeamMember} className="space-y-3">
                    <div><Label>{tt('Name', 'Nombre')}</Label><Input value={teamForm.name} onChange={e => setTeamForm(f => ({ ...f, name: e.target.value }))} required className="mt-1" data-testid="team-name" /></div>
                    <div><Label>Email</Label><Input type="email" value={teamForm.email} onChange={e => setTeamForm(f => ({ ...f, email: e.target.value }))} required className="mt-1" data-testid="team-email" /></div>
                    <div><Label>{tt('Password', 'Contrasena')}</Label><Input type="password" value={teamForm.password} onChange={e => setTeamForm(f => ({ ...f, password: e.target.value }))} required className="mt-1" data-testid="team-password" /></div>
                    <div><Label>{tt('Phone', 'Telefono')}</Label><Input value={teamForm.phone} onChange={e => setTeamForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" data-testid="team-phone" /></div>
                    <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="submit-team-member">{tt('Create', 'Crear')}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {data?.team?.length > 0 ? (
              <div className="bg-white rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{tt('Name', 'Nombre')}</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>{tt('Phone', 'Telefono')}</TableHead>
                      <TableHead>{tt('Properties', 'Propiedades')}</TableHead>
                      <TableHead>{tt('Actions', 'Acciones')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.team.map(member => (
                      <TableRow key={member.id} data-testid={`team-member-${member.id}`}>
                        <TableCell className="font-medium">{member.name}</TableCell>
                        <TableCell className="text-sm text-slate-500">{member.email}</TableCell>
                        <TableCell className="text-sm">{member.phone || '-'}</TableCell>
                        <TableCell><Badge variant="outline">{data.team_property_counts?.[member.id] || 0}</Badge></TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" className="h-7 text-red-600 hover:bg-red-50"
                            onClick={() => removeTeamMember(member.id)} data-testid={`remove-member-${member.id}`}>
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-border">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">{tt('No team members yet', 'No hay miembros del equipo')}</p>
              </div>
            )}
          </TabsContent>
        )}

        {/* ===== BRANDING TAB (Pro Only) ===== */}
        {isPro && (
          <TabsContent value="branding">
            <Card>
              <CardContent className="p-6 space-y-5">
                <div>
                  <h3 className="font-heading font-semibold text-base mb-1">{tt('Corporate Branding', 'Marca Corporativa')}</h3>
                  <p className="text-sm text-slate-500">{tt('Customize your agent profile with your company branding.', 'Personaliza tu perfil de agente con tu marca.')}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{tt('Company Name', 'Nombre de Empresa')}</Label>
                    <Input value={brandingForm.company_name} onChange={e => setBrandingForm(f => ({ ...f, company_name: e.target.value }))}
                      className="mt-1" placeholder="WakaCasa Realty" data-testid="branding-company-name" />
                  </div>
                  <div>
                    <Label>{tt('Logo URL', 'URL del Logo')}</Label>
                    <Input value={brandingForm.logo_url} onChange={e => setBrandingForm(f => ({ ...f, logo_url: e.target.value }))}
                      className="mt-1" placeholder="https://..." data-testid="branding-logo-url" />
                  </div>
                </div>
                {brandingForm.logo_url && (
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-slate-500">{tt('Preview:', 'Vista previa:')}</span>
                    <img src={brandingForm.logo_url} alt="Logo" className="h-12 w-auto rounded border" />
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Button onClick={saveBranding} className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="save-branding-btn">
                    {tt('Save Branding', 'Guardar Marca')}
                  </Button>
                  <span className="text-xs text-slate-400">{tt('Verified badge is assigned by admin', 'La insignia de verificado la asigna el admin')}</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
